/**
 * Подписи, подсказки и типы ячеек таблицы размеров — чистая логика без React.
 *
 * Две копии ПОДСКАЗКИ разъехались бы на первой же правке, и спорящие подписи
 * на двух экранах (карточка товара и вид «список» раздела «Товары») читаются
 * как разные числа. Поэтому подсказки живут здесь, одни на все экраны, а
 * ячейки берут их из этого модуля (`components/sizes/*`).
 *
 * Выделено из `components/sizeCells.tsx` 14.10.26 без изменения поведения.
 */

import { fixed2, nf, pct } from "@/lib/format";
import { MIN_GAP, MOQ } from "@/lib/reorder";

/** Свежесть снимка остатка: строка кэша залипает, если WB перестал отдавать
 * размер, и «0 от вчера» обязан отличаться от свежего нуля. */
export function stale(iso: string | null): boolean {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > 24 * 3600 * 1000;
}

export function timeOf(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Где груз по мнению CRM. Имена и подписи — копия `plan/coming/service.py`. */
export const KLASS_ORDER = ["placed", "transit", "arrived"] as const;
export const KLASS_LABEL: Record<string, string> = {
  placed: "заказан",
  transit: "в пути",
  arrived: "в РФ, ждёт приёмки",
};

export type InOrders = {
  in_orders: number;
  stale: number;
  ordered: number;
  accepted: number;
  by_klass: Record<string, number>;
};

/** Подсказка ячейки «в заказах» — одна на карточку и вид «список» раздела
 * «Товары»: две копии разъехались бы на первой правке. */
export function inOrdersTitle(card: InOrders): string {
  if (!card.in_orders && !card.stale) return "непринятого по этому размеру нет";
  return [
    `из ${card.ordered} заказано, принято ${card.accepted}`,
    ...KLASS_ORDER.filter((klass) => (card.by_klass[klass] ?? 0) > 0).map(
      (klass) => `${KLASS_LABEL[klass]} ${card.by_klass[klass]}`,
    ),
    card.stale ? `плюс ${card.stale} зависли, старше 120 дней` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Слагаемые «к заказу» одного размера — поля `plan/reorder.py::reorder_of`. */
export type ReorderParts = {
  need_wb: number;
  need_ozon: number;
  gap_wb: number;
  gap_ozon: number;
  gap: number;
  order: number;
};

/** Подсказка подписи «к заказу» — формула словами. */
export function reorderFormulaTitle(days: number): string {
  return (
    `нехватка WB (продадим за ${days} дн − склад WB) + ` +
    `нехватка Ozon (продадим за ${days} дн − склад Ozon) ` +
    `− короба − в планах − в заказах; ` +
    `запас одной площадки не закрывает дефицит другой — товар с Ozon не забрать; ` +
    `нехватка меньше ${MIN_GAP} — ноль, иначе не меньше ${MOQ} шт`
  );
}

/** Подсказка ячейки «к заказу»: обе половины площадок и общий пул числами. */
export function reorderTitle(
  size: string,
  got: ReorderParts | undefined,
  p: { stockWb: number | null; stockOzon: number | null; korob: number; planLeft: number; inOrders: number },
): string {
  return (
    `${size}: WB ${nf.format(got?.need_wb ?? 0)} − склад ` +
    `${nf.format(p.stockWb ?? 0)} = ${nf.format(got?.gap_wb ?? 0)}; ` +
    `Ozon ${nf.format(got?.need_ozon ?? 0)} − склад ` +
    `${nf.format(p.stockOzon ?? 0)} = ${nf.format(got?.gap_ozon ?? 0)}; ` +
    `− короба ${nf.format(p.korob)} − в планах ${nf.format(p.planLeft)} − ` +
    `в заказах ${nf.format(p.inOrders)} = нехватка ${nf.format(got?.gap ?? 0)} → ` +
    `к заказу ${nf.format(got?.order ?? 0)}`
  );
}

/** Чистый выкуп одного размера за окно.
 *
 * Верхние три числа — СУММА двух площадок, `wb` и `ozon` — её слагаемые.
 * Сложены два разных основания (финотчёт WB и витрина Ozon), и подсказка
 * обязана показывать, чего сколько: иначе «14 шт за 60 дней» не отличить от
 * «14 на WB и ноль на Ozon». */
export type SpeedHalf = { sold: number; returned: number; net: number };
export type SpeedWindow = SpeedHalf & { wb: SpeedHalf; ozon: SpeedHalf };

/** Подсказка одна на обе ячейки блока: сырьё, из которого получилось число.
 * Две копии разъехались бы на первой правке. */
export function speedTitle(
  cell: SpeedWindow | undefined,
  days: number,
): string {
  if (!cell) return `движений по этому размеру за ${days} дней не было`;
  return (
    `продано ${nf.format(cell.sold)}, возврат ${nf.format(cell.returned)}` +
    ` → чистый выкуп ${nf.format(cell.net)} шт за ${days} дней` +
    ` · WB ${nf.format(cell.wb.net)} · Ozon ${nf.format(cell.ozon.net)}` +
    ` · ${(cell.net / days).toFixed(2)} шт/дн` +
    " · это ФАКТ за прошлые дни, а не прогноз"
  );
}

/** Движения финотчёта по одному размеру — ЕДИНСТВЕННЫЙ источник выкупа на
 * карточке товара с 08.09.26: блок заказов CRM убран, и «выкуплено» там
 * считается теперь только отсюда.
 *
 * Форма — ячейка своего финотчёта (`report/card.py::cell`) один в один.
 * `pct` — чистая выкупаемость (продано минус возврат ÷ доставки), `pct_raw` —
 * валовая. `null` в обоих значит «доставок не было», а не ноль процентов, и
 * `null` в `trip_price` — «рейсов не было», а не даровая доставка.
 */
export type ReportBlock = {
  /** Доставки К КЛИЕНТУ — знаменатель обоих процентов. Это НЕ все рейсы: в
   * тех есть ещё и обратная дорога отказа. */
  delivered: number;
  /** Валовая продажа: включает то, что потом вернулось. Едет в подсказку. */
  sold: number;
  returned: number;
  /** Чистый выкуп — продано минус возврат. ЭТО ЧИСЛО ВИДИТ ЗАКУПЩИК. */
  net: number;
  pct: number | null;
  pct_raw: number | null;
  /** Доля возврата ОТ ПРОДАННОГО. `null` — продаж не было вовсе. */
  return_pct: number | null;
  /** Деньги логистики за все поездки размера и число самих поездок: счёт WB
   * выставляет за каждую, включая отказы и возвраты. На экране их с 11.09.26
   * нет ни одной строкой — обе живут в подсказке. */
  delivery: number;
  trips: number;
  /** Рейсы, кончившиеся продажей. В «выкуплено» они НЕ идут: это про судьбу
   * доставки, а не про штуки, оставшиеся у покупателя. */
  buyout: number;
  /** `delivery ÷ trips` — цена одной поездки. */
  trip_price: number | null;
};

/** Подсказка одна на все ячейки блока: раскладка движений, валовой процент и
 * тариф поездки. Две копии разъехались бы на первой правке.
 *
 * ЗДЕСЬ ЖИВЁТ ВСЁ, ЧТО УШЛО С ЭКРАНА 11.09.26 — доставки (знаменатель обоих
 * процентов), валовые штуки и цена рейса: владелец оставил в таблице три
 * строки, но числа, из которых они получены, человек обязан мочь достать.
 */
export function reportTitle(cell: ReportBlock | undefined): string {
  if (!cell) return "движений по этому размеру в отборе не было";
  return (
    `доставлено к клиенту ${nf.format(cell.delivered)}` +
    ` · продано ${nf.format(cell.sold)}, возврат ${nf.format(cell.returned)}` +
    ` → чистый выкуп ${nf.format(cell.net)}` +
    (cell.pct_raw === null ? "" : ` · валовая выкупаемость ${pct(cell.pct_raw)}`) +
    // Деньги с копейками (`fixed2`): цена рейса круглой не бывает, и округление
    // до рубля прятало бы разницу между 34,60 ₽ и 34,90 ₽ — а весь смысл
    // строки в том, чтобы эту разницу увидеть.
    (cell.trip_price === null
      ? ""
      : ` · ${fixed2.format(cell.delivery)} ₽ логистики ÷ ${nf.format(cell.trips)} рейсов` +
        ` = ${fixed2.format(cell.trip_price)} ₽ за рейс`) +
    " · клик — график по времени"
  );
}

/** Половина Ozon той же таблицы — витрина кабинета по SKU.
 *
 * `orders` — ЗНАМЕНАТЕЛЬ процента выкупа, и на экране его нет: владелец снял
 * оба знаменателя 11.09.26 («мне нужно только выкупаемость, процент возврата,
 * процент выкупа»). Живёт он в подсказке, где и раскладка движения.
 */
export type OzonBlock = {
  orders: number;
  /** Доставлено покупателю — у Ozon это и ЕСТЬ выкуп: отказа на пункте выдачи
   * там нет, и строки «доставлено» в блоке не бывает. */
  sold: number;
  returned: number;
  /** Чистый выкуп: доставлено минус возврат. ЭТО ЧИСЛО ВИДИТ ЗАКУПЩИК. */
  net: number;
  pct: number | null;
  return_pct: number | null;
};

/** Подсказка одна на все ячейки блока Ozon — как `reportTitle` у WB. */
export function ozonTitle(cell: OzonBlock | null | undefined): string {
  if (!cell) return "этого размера на Ozon нет";
  return (
    `заказано ${nf.format(cell.orders)} · доставлено ${nf.format(cell.sold)},` +
    ` возврат ${nf.format(cell.returned)} → чистый выкуп ${nf.format(cell.net)}` +
    " · процент выкупа считается от ЗАКАЗОВ: доставленное покупателю у Ozon и есть выкуп"
  );
}
